import { useState } from 'react';

import { Modal } from '../../features/albums/Modal';
import { copy } from '../../config/copy';
import { useSession } from '../../stores/session';
import { Button } from './Button';

/**
 * What a rewind does, said out loud before it happens.
 *
 * ## Why this exists at all
 *
 * A rewind is the strongest signal a listener can send. It enters every one of
 * their followers' feeds as its own event, and it counts three times a play in
 * the trending score - more than a favourite, more than listening to the track
 * on repeat. None of that was ever said anywhere, so it read as a bookmark with
 * a strange icon, and people who pressed it had no idea they had just published
 * something.
 *
 * A dialog is friction, and here that is the point: this is a publishing
 * action, and publishing actions should be deliberate. Undoing one is still a
 * single click with no dialog, because taking something back should be easier
 * than putting it out.
 *
 * ## The caption was built and never asked for
 *
 * `RepostTrackAction` has validated an optional caption since it was written,
 * `reposts.caption` has always held one, and the feed renders it. Nothing ever
 * sent one, because there was no field. That single gap is most of the
 * difference between a rewind and a favourite: "this bridge is unreal" is a
 * recommendation, and a silent rewind is a bookmark.
 */
export function RewindComposer({
  title,
  artist,
  pending = false,
  onCancel,
  onRewind,
}: {
  title: string;
  artist: string | null;
  pending?: boolean;
  onCancel: () => void;
  onRewind: (caption: string | null) => void;
}) {
  const [caption, setCaption] = useState('');
  const followers = useSession((state) => state.user?.follower_count ?? 0);

  /*
   * The reach, in the words of the person doing it.
   *
   * Nobody has "0 followers" said back to them: for somebody with none the
   * honest and useful sentence is about the chart, which their rewind still
   * moves. Telling them they are shouting into an empty room is true and
   * discouraging in a place where discouragement buys nothing.
   */
  const reach =
    followers > 0
      ? `Goes out to your ${followers.toLocaleString()} follower${followers === 1 ? '' : 's'}`
      : 'Goes into the feed of anybody who follows you';

  return (
    <Modal title={copy['action.repost']} onClose={onCancel}>
      <div className="flex flex-col gap-4 p-5" data-rewind-composer>
        <div>
          <p className="text-sm font-medium text-ink">{title}</p>
          {artist && <p className="font-mono text-[11px] text-soft">{artist}</p>}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="meta-caps text-faint">Say something (optional)</span>
          <textarea
            rows={3}
            maxLength={280}
            autoFocus
            value={caption}
            data-rewind-caption
            placeholder="Why this one?"
            onChange={(event) => setCaption(event.target.value)}
            className="w-full resize-none rounded-[var(--radius-control)] border border-line bg-app p-2.5 text-sm text-ink outline-none transition-colors focus:border-line-strong"
          />
        </label>

        {/*
          The two things it actually does, in the order they matter to the
          person pressing it: who sees it, and what it is worth. The chart
          weight is not a detail - it is the reason a rewind is the most
          valuable thing a listener can do for an artist without paying them.
        */}
        <ul className="flex flex-col gap-1.5 text-xs text-soft" data-rewind-impact>
          <li className="flex gap-2">
            <span aria-hidden className="text-positive">
              →
            </span>
            {reach}
          </li>
          <li className="flex gap-2">
            <span aria-hidden className="text-positive">
              →
            </span>
            Counts three times a play toward the charts, more than a favorite
          </li>
        </ul>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            data-rewind-confirm
            loading={pending}
            onClick={() => onRewind(caption.trim() === '' ? null : caption.trim())}
          >
            {copy['action.repost']}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
